import { DateTime } from "luxon";
import type { DateRange } from "react-day-picker";
import type { DateBounds } from "../types";
import { isValidDate } from "./isValidDate";
import { normalizeDateRange } from "./normalizeDateRange";
import { startOfLocalDay } from "./startOfLocalDay";

export function maxRangeSlidingWindow(
  selected: DateRange | undefined,
  maxRangeDays: number | undefined,
): DateBounds {
  if (!maxRangeDays || !Number.isFinite(maxRangeDays) || maxRangeDays <= 0) {
    return {};
  }

  // An inclusive N-day range gives N−1 day steps in Luxon.
  const rangeSteps = maxRangeDays - 1;
  const from = selected?.from;
  const to = selected?.to;

  if (isValidDate(from) && isValidDate(to)) {
    const { from: rangeStartDate, to: rangeEndDate } = normalizeDateRange(
      from,
      to,
    );
    const selectionStart = startOfLocalDay(rangeStartDate);
    const selectionEnd = startOfLocalDay(rangeEndDate);
    const selectionStartMs = selectionStart.getTime();
    const selectionEndMs = selectionEnd.getTime();

    const slidingWindowStartMs = DateTime.fromMillis(selectionEndMs)
      .minus({ days: rangeSteps })
      .toMillis();
    const slidingWindowEndMs = DateTime.fromMillis(selectionStartMs)
      .plus({ days: rangeSteps })
      .toMillis();

    return {
      min: new Date(Math.min(slidingWindowStartMs, selectionStartMs)),
      max: new Date(Math.max(slidingWindowEndMs, selectionEndMs)),
    };
  }

  if (isValidDate(from)) {
    const fromAtMidnight = DateTime.fromJSDate(startOfLocalDay(from));
    return {
      min: fromAtMidnight.minus({ days: rangeSteps }).toJSDate(),
      max: fromAtMidnight.plus({ days: rangeSteps }).toJSDate(),
    };
  }

  return {};
}
